/* eslint no-underscore-dangle: 0 */
/* eslint no-nested-ternary: 0 */
import React, { useEffect, useRef, useState } from 'react';

// material-ui
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from '@mui/material';

// third-party
import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

// project imports
import EntityNameLink from 'components/crm/EntityNameLink';
import TagSelect from 'components/crm/TagSelect';
import Spinner from 'components/spinner';
import FailureLoad from 'components/spinner/fail';
import { StaticSelect } from 'components/select/staticSelect';
import useDebouncedSearch from 'hooks/common/useDebounceSearch';
import useCrmGQL from 'hooks/crm/useGQL';
import { LIST_CRM_BUSINESSES } from 'grapqhl/crm/account/queries';
import ERoutes from 'routes/ERoutes';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { CrmAccount, CrmBusinessStatus, ListCrmAccountsResponse } from 'types/crm';
import MainCard from 'ui-component/cards/MainCard';

// ==============================|| BUSINESSES (CRM-backed) LIST ||============================== //

/**
 * The unified Businesses screen (POGO-12603 Wave 3 Phase 1): lists every CRM
 * account — leads and linked customers alike — with a derived **Status** chip
 * (Lead / Free Trial / Subscribed / Cancelled / Pogozo Demo) and a status
 * filter. Read-only slice; write paths (add business, invite) land in Phase 3.
 */

type StatusChipConfig = {
    label: string;
    color: 'default' | 'info' | 'success' | 'error' | 'secondary';
};

const STATUS_CONFIG: Record<CrmBusinessStatus, StatusChipConfig> = {
    lead: { label: 'Lead', color: 'default' },
    free_trial: { label: 'Free Trial', color: 'info' },
    free_trial_ended: { label: 'Free Trial Ended', color: 'default' },
    subscribed: { label: 'Subscribed', color: 'success' },
    cancelled: { label: 'Cancelled', color: 'error' },
    pogozo_demo: { label: 'Pogozo Demo', color: 'secondary' }
};

const STATUS_FILTER_OPTIONS = [
    { label: 'All statuses', value: '' },
    { label: 'Lead', value: 'lead' },
    { label: 'Free Trial', value: 'free_trial' },
    { label: 'Free Trial Ended', value: 'free_trial_ended' },
    { label: 'Subscribed', value: 'subscribed' },
    { label: 'Cancelled', value: 'cancelled' },
    { label: 'Pogozo Demo', value: 'pogozo_demo' }
];

const StatusChip = ({ status }: { status?: CrmBusinessStatus | null }) => {
    const config = (status && STATUS_CONFIG[status]) || STATUS_CONFIG.lead;
    return <Chip label={config.label} color={config.color} size="small" variant={config.color === 'default' ? 'outlined' : 'filled'} />;
};

/**
 * "Add business" dialog state (Wave 3 Phase 3). Captures the business name +
 * the fields that already exist on `CrmBusinessDetails`, plus an optional owner
 * contact. The richer onboarding-sourced questions (current system / classes /
 * demo) land with the rest of the detail form in Phase 10.
 */
type AddBusinessForm = {
    businessName: string;
    website: string;
    email: string;
    phone: string;
    address: string;
    teamSize: string;
    currentSoftware: string;
    runsClasses: boolean;
    contactFirstName: string;
    contactLastName: string;
    contactEmail: string;
    contactPhone: string;
};

const emptyBusinessForm: AddBusinessForm = {
    businessName: '',
    website: '',
    email: '',
    phone: '',
    address: '',
    teamSize: '',
    currentSoftware: '',
    runsClasses: false,
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: ''
};

const VALID_STATUS_FILTERS = STATUS_FILTER_OPTIONS.map((option) => option.value).filter(Boolean);

/**
 * Reasons an account can be flagged out of reporting (POGO-13862) — a
 * fake/spam/test lead that would otherwise skew the Pogozo-businesses numbers.
 * Mirrors the `CrmExcludedReason` enum on the API.
 */
const EXCLUDE_REASON_OPTIONS = [
    { label: 'Fake', value: 'fake' },
    { label: 'Spam', value: 'spam' },
    { label: 'Test', value: 'test' },
    { label: 'Duplicate', value: 'duplicate' },
    { label: 'Other', value: 'other' }
];

const BusinessList = () => {
    const dispatch = useDispatch();

    // POGO-13862: the Reporting dashboard tiles deep-link here with a preset
    // `?status=` (e.g. free_trial / subscribed), so seed the status filter from
    // the query string when it names a known status.
    const [searchParams] = useSearchParams();
    const presetStatus = searchParams.get('status') ?? '';

    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [searchText, setSearchText] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>(VALID_STATUS_FILTERS.includes(presetStatus) ? presetStatus : '');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [form, setForm] = useState<AddBusinessForm>(emptyBusinessForm);

    // POGO-13862 row action: flag a fake/spam/test lead out of reporting. The
    // kebab opens a per-row menu; "Exclude from reporting" opens a reason
    // dialog. A flagged account drops off this list on refetch (the server
    // match excludes it), so the list only ever offers the exclude direction.
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [menuAccount, setMenuAccount] = useState<CrmAccount | null>(null);
    const [excludeAccount, setExcludeAccount] = useState<CrmAccount | null>(null);
    const [excludeReason, setExcludeReason] = useState<string>('fake');

    const { CREATE_ACCOUNT, SET_REPORTING_EXCLUDED } = useCrmGQL();
    const [handleCreateAccount, { loading: creating }] = CREATE_ACCOUNT();
    const [handleSetReportingExcluded, { loading: excluding }] = SET_REPORTING_EXCLUDED();

    const [debouncedSearch] = useDebouncedSearch((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
        if (event?.target) {
            setSearchText(event.target.value);
            setPage(0);
        }
    });

    const { loading, error, data, refetch } = useQuery<ListCrmAccountsResponse>(LIST_CRM_BUSINESSES, {
        variables: {
            input: {
                pageMeta: {
                    skip: page > 0 ? rowsPerPage * page : 0,
                    limit: rowsPerPage
                },
                filter: {
                    ...(searchText ? { searchText } : {}),
                    ...(statusFilter ? { businessStatuses: [statusFilter] } : {}),
                    ...(selectedTags.length ? { tags: selectedTags } : {})
                }
            }
        },
        fetchPolicy: 'cache-and-network'
    });

    const accounts: CrmAccount[] = data?.listCrmAccounts?.data ?? [];
    const total = data?.listCrmAccounts?.pagination?.total ?? 0;
    // Keyed on the Apollo error message so a background refetch failure snacks
    // once, not on every render (POGO-13326) — see the effect below showSnack.
    const lastRefreshError = useRef<string>();

    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
        if (event?.target?.value) {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        }
    };

    const showSnack = (message: string, color: 'success' | 'error') => {
        dispatch(
            openSnackbar({
                open: true,
                message,
                anchorOrigin: { horizontal: 'center' },
                variant: 'alert',
                alert: { color }
            })
        );
    };

    // A failed *background* refetch (we still have the last-loaded page) can't
    // take over the screen with FailureLoad, so surface it as a snackbar
    // instead — matching the sibling CRM Contacts screen (POGO-13326).
    useEffect(() => {
        if (error && data && lastRefreshError.current !== error.message) {
            lastRefreshError.current = error.message;
            showSnack('Could not refresh businesses. Showing the last loaded results.', 'error');
        }
        if (!error) {
            lastRefreshError.current = undefined;
        }
        // showSnack is intentionally local; keying on the Apollo error avoids repeated notifications.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, error]);

    const openAdd = () => {
        setForm(emptyBusinessForm);
        setDialogOpen(true);
    };

    /**
     * Save (`sendInvite=false`) creates a bare CRM lead that appears in this
     * list immediately. Save & send invite (`sendInvite=true`) additionally
     * provisions the tenant Business + owner via the existing invite flow and
     * links it — so it lands as a real customer. The invite path needs the
     * owner contact's full details.
     */
    const handleSave = async (sendInvite: boolean) => {
        if (!form.businessName.trim()) {
            showSnack('The business needs a name', 'error');
            return;
        }
        if (
            sendInvite &&
            !(form.contactFirstName.trim() && form.contactLastName.trim() && form.contactEmail.trim() && form.contactPhone.trim())
        ) {
            showSnack("Sending an invite needs the owner's first name, last name, email and phone", 'error');
            return;
        }

        const hasContact = !!(form.contactFirstName.trim() || form.contactEmail.trim() || form.contactPhone.trim());
        const teamSizeNum = form.teamSize.trim() ? parseInt(form.teamSize, 10) : undefined;

        try {
            await handleCreateAccount({
                variables: {
                    input: {
                        businessName: form.businessName.trim(),
                        sendInvite,
                        businessDetails: {
                            ...(form.website.trim() ? { website: form.website.trim() } : {}),
                            ...(form.email.trim() ? { email: form.email.trim() } : {}),
                            ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
                            ...(form.address.trim() ? { address: form.address.trim() } : {}),
                            ...(teamSizeNum !== undefined && !Number.isNaN(teamSizeNum) ? { teamSize: teamSizeNum } : {}),
                            ...(form.currentSoftware.trim() ? { currentSoftware: form.currentSoftware.trim() } : {}),
                            runsClasses: form.runsClasses
                        },
                        ...(hasContact
                            ? {
                                  contact: {
                                      firstName: form.contactFirstName.trim(),
                                      ...(form.contactLastName.trim() ? { lastName: form.contactLastName.trim() } : {}),
                                      ...(form.contactEmail.trim() ? { email: form.contactEmail.trim() } : {}),
                                      ...(form.contactPhone.trim() ? { phone: form.contactPhone.trim() } : {})
                                  }
                              }
                            : {})
                    }
                }
            });
            setDialogOpen(false);
            setPage(0);
            await refetch();
            showSnack(sendInvite ? 'Business added and invite sent' : 'Business added', 'success');
        } catch (err: any) {
            showSnack(err?.message || 'Failed to add the business', 'error');
        }
    };

    const openRowMenu = (event: React.MouseEvent<HTMLElement>, account: CrmAccount) => {
        setMenuAnchor(event.currentTarget);
        setMenuAccount(account);
    };

    const closeRowMenu = () => {
        setMenuAnchor(null);
        setMenuAccount(null);
    };

    const openExcludeDialog = () => {
        setExcludeAccount(menuAccount);
        setExcludeReason('fake');
        closeRowMenu();
    };

    const confirmExclude = async () => {
        if (!excludeAccount) return;
        try {
            await handleSetReportingExcluded({
                variables: { input: { accountId: excludeAccount._id, excluded: true, reason: excludeReason as any } }
            });
            setExcludeAccount(null);
            await refetch();
            showSnack('Business excluded from reporting', 'success');
        } catch (err: any) {
            showSnack(err?.message || 'Failed to exclude the business from reporting', 'error');
        }
    };

    return (
        <MainCard title="Businesses" content={false} sx={{ position: 'relative' }}>
            <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" gap={1}>
                    <Box width={{ xs: '100%', md: '240px', lg: '300px' }}>
                        <TextField
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                            onChange={debouncedSearch}
                            placeholder="Search business, tags, email or phone"
                            size="small"
                        />
                    </Box>

                    <Box width={{ xs: '100%', md: '200px' }}>
                        <StaticSelect
                            name="businessStatus"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e?.target?.value);
                                setPage(0);
                            }}
                            placeholder="Select status"
                            options={STATUS_FILTER_OPTIONS}
                            onBlur={() => {}}
                        />
                    </Box>

                    <Box sx={{ minWidth: { lg: 220 } }}>
                        <TagSelect
                            value={selectedTags}
                            onChange={(next) => {
                                setSelectedTags(next);
                                setPage(0);
                            }}
                            placeholder="Any tags"
                        />
                    </Box>

                    <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ whiteSpace: 'nowrap' }}>
                        Add business
                    </Button>
                </Stack>
            </CardContent>

            {loading && !data ? (
                <Spinner />
            ) : error && !data ? (
                <FailureLoad />
            ) : (
                <>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Business</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Tags</TableCell>
                                    <TableCell>Date signed up</TableCell>
                                    <TableCell>Last activity</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {accounts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography variant="body2" sx={{ py: 2, textAlign: 'center' }}>
                                                No businesses found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    accounts.map((account) => (
                                        <TableRow hover key={account._id}>
                                            <TableCell>
                                                <EntityNameLink
                                                    to={ERoutes.crmAccountDetail.replace(':id', account._id)}
                                                    variant="subtitle1"
                                                    sx={{ textAlign: 'left' }}
                                                >
                                                    {account.businessName}
                                                </EntityNameLink>
                                            </TableCell>
                                            <TableCell>
                                                <StatusChip status={account.businessStatus} />
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                    {(account.tags ?? []).map((tag) => (
                                                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                                                    ))}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                {account.signedUpAt
                                                    ? dayjs(account.signedUpAt).format('DD MMM YYYY')
                                                    : account.createdAt
                                                    ? dayjs(account.createdAt).format('DD MMM YYYY')
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                                                {account.lastLoginAt ? dayjs(account.lastLoginAt).format('DD MMM YYYY') : '—'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Actions for ${account.businessName}`}
                                                    onClick={(event) => openRowMenu(event, account)}
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component={Paper}
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </>
            )}

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeRowMenu}>
                <MenuItem onClick={openExcludeDialog}>Exclude from reporting</MenuItem>
            </Menu>

            <Dialog open={Boolean(excludeAccount)} onClose={() => setExcludeAccount(null)} fullWidth maxWidth="xs">
                <DialogTitle>Exclude from reporting</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            {excludeAccount?.businessName} will be dropped from every reporting number and no longer counted in the Pogozo
                            businesses dashboard. Pick a reason.
                        </Typography>
                        <StaticSelect
                            name="excludeReason"
                            value={excludeReason}
                            onChange={(e) => setExcludeReason(e?.target?.value)}
                            placeholder="Select a reason"
                            options={EXCLUDE_REASON_OPTIONS}
                            onBlur={() => {}}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExcludeAccount(null)}>Cancel</Button>
                    <Button variant="contained" color="error" disabled={excluding} onClick={confirmExclude}>
                        {excluding ? 'Excluding...' : 'Exclude from reporting'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add business</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Business name"
                                required
                                value={form.businessName}
                                onChange={(e) => setForm({ ...form, businessName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Business email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Business phone"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Website"
                                value={form.website}
                                onChange={(e) => setForm({ ...form, website: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Team size"
                                type="number"
                                value={form.teamSize}
                                onChange={(e) => setForm({ ...form, teamSize: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Address"
                                value={form.address}
                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Current software"
                                value={form.currentSoftware}
                                onChange={(e) => setForm({ ...form, currentSoftware: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.runsClasses}
                                        onChange={(e) => setForm({ ...form, runsClasses: e.target.checked })}
                                    />
                                }
                                label="Runs classes"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="textSecondary">
                                Owner contact (required to send an invite)
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Owner first name"
                                value={form.contactFirstName}
                                onChange={(e) => setForm({ ...form, contactFirstName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Owner last name"
                                value={form.contactLastName}
                                onChange={(e) => setForm({ ...form, contactLastName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Owner email"
                                value={form.contactEmail}
                                onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Owner phone"
                                value={form.contactPhone}
                                onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="outlined" disabled={creating} onClick={() => handleSave(false)}>
                        {creating ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="contained" disabled={creating} onClick={() => handleSave(true)}>
                        {creating ? 'Saving...' : 'Save & send invite'}
                    </Button>
                </DialogActions>
            </Dialog>
        </MainCard>
    );
};

export default BusinessList;
